import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../service/auth/auth.service';
import { RestService } from '../service/rest/rest.service';
import { LegalFooterComponent } from './legal-footer.component';
import { PRIVACY_VERSION, TERMS_VERSION } from './legal-content';
import { ConnectivityService } from '../service/connectivity/connectivity.service';
import { PolicyAcceptanceStorageService } from '../service/legal/policy-acceptance-storage.service';
import { isTermsEndpointMissing } from '../service/legal/policy-terms.util';
import { LoggerService } from '../service/utils/logger.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-policy-acceptance',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, LegalFooterComponent],
  template: `
    <div class="acceptance-shell">
      <main class="acceptance-card">
        <h1>Review the terms</h1>
        <p>Accept the current Terms of Service and acknowledge the Privacy Notice to finish signing in.</p>
        <form [formGroup]="form" (ngSubmit)="accept()">
          <label class="form-check">
            <input class="form-check-input" type="checkbox" formControlName="accepted">
            <span>I accept the <a routerLink="/terms" target="_blank">Terms of Service</a> ({{ termsVersion }}) and acknowledge the <a routerLink="/privacy" target="_blank">Privacy Notice</a> ({{ privacyVersion }}).</span>
          </label>
          <p *ngIf="!connectivity.isOnline" class="alert alert-info mt-3" role="status">
            Connect once to review and accept the current terms.
          </p>
          <p *ngIf="error" class="alert alert-danger mt-3" role="alert">{{ error }}</p>
          <button class="btn btn-primary w-100 mt-4" type="submit" [disabled]="form.invalid || loading">
            {{ loading ? 'Saving...' : 'Accept and continue' }}
          </button>
        </form>
      </main>
      <app-legal-footer></app-legal-footer>
    </div>
  `,
  styles: [`
    .acceptance-shell { min-height: 100vh; display: grid; align-content: center; background: #f6f8fb; padding: 24px; }
    .acceptance-card, app-legal-footer { display: block; width: min(100%, 620px); margin-inline: auto; }
    .acceptance-card { padding: clamp(24px, 6vw, 48px); border: 1px solid #dde6f1; border-radius: 16px; background: #fff; }
    .form-check { display: flex; gap: 12px; margin-top: 24px; line-height: 1.55; }
    .form-check-input { flex: 0 0 auto; margin-top: .3rem; }
  `],
})
export class PolicyAcceptanceComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  termsVersion = TERMS_VERSION;
  readonly privacyVersion = PRIVACY_VERSION;
  readonly form = this.fb.group({ accepted: [false, Validators.requiredTrue] });
  loading = false;
  error = '';
  private connectivitySubscription?: Subscription;
  private checkingPolicy = false;

  constructor(
    private auth: AuthService,
    private rest: RestService,
    private router: Router,
    public connectivity: ConnectivityService,
    private policyStorage: PolicyAcceptanceStorageService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.connectivitySubscription = this.connectivity.connected$.subscribe((online) => {
      if (online) void this.checkPolicyStatus();
    });
    await this.checkPolicyStatus();
  }

  ngOnDestroy(): void {
    this.connectivitySubscription?.unsubscribe();
  }

  private async checkPolicyStatus(): Promise<void> {
    if (this.checkingPolicy) return;
    this.checkingPolicy = true;
    try {
      const user = this.auth.getUser();
      const token = user?.sid;
      if (!token || !user?.id) return;
      if (!this.connectivity.isOnline) {
        if (await this.policyStorage.isCurrent(user.id, TERMS_VERSION, PRIVACY_VERSION)) {
          await this.router.navigate(['/dashboard'], { replaceUrl: true });
        } else {
          this.error = 'Connect once to verify your acceptance of the current terms.';
        }
        return;
      }
      const status = await this.rest.getCurrentTermsStatus(token);
      this.termsVersion = status.policyVersion || TERMS_VERSION;
      if (status.accepted) {
        await this.finishLocalAcceptance(user.id, this.termsVersion);
      }
    } catch (error) {
      if (isTermsEndpointMissing(error)) {
        LoggerService.log('[policy-acceptance] Terms API missing; continuing with local acceptance gate');
        this.error = '';
        return;
      }
      this.error = 'We could not load the current policy status. Check your connection and try again.';
    } finally {
      this.checkingPolicy = false;
    }
  }

  async accept(): Promise<void> {
    if (this.form.invalid) return;
    const user = this.auth.getUser();
    const token = user?.sid;
    if (!token || !user?.id) {
      await this.router.navigate(['/login']);
      return;
    }
    this.loading = true;
    this.error = '';
    try {
      if (!this.connectivity.isOnline) {
        // Still try the request; Connectivity can be wrong on some devices.
        // If the offline interceptor blocks it, fall through to the catch.
      }
      const status = await this.rest.acceptCurrentTerms(token, this.termsVersion, 'mobile');
      if (!status.accepted) throw new Error('Acceptance was not recorded.');
      await this.rest.updateSupabasePolicyAcceptance(token, {
        termsVersion: status.policyVersion,
        privacyVersion: PRIVACY_VERSION,
        acceptedAt: new Date().toISOString(),
      }).catch(() => undefined);
      await this.finishLocalAcceptance(user.id, status.policyVersion || this.termsVersion);
    } catch (error) {
      if (isTermsEndpointMissing(error)) {
        // Backend build predates the terms API; record locally so the user is not stuck.
        LoggerService.log('[policy-acceptance] Terms API missing; saving acceptance locally');
        await this.finishLocalAcceptance(user.id, this.termsVersion);
        return;
      }
      if (!this.connectivity.isOnline) {
        this.error = 'Connect once to accept the current terms.';
      } else {
        this.error = 'We could not save your acceptance. Check your connection and try again.';
      }
    } finally {
      this.loading = false;
    }
  }

  private async finishLocalAcceptance(userId: number, termsVersion: string): Promise<void> {
    await this.policyStorage.save({
      userId,
      termsVersion,
      privacyVersion: PRIVACY_VERSION,
      acceptedAt: new Date().toISOString(),
    });
    await this.router.navigate(['/dashboard'], { replaceUrl: true });
  }
}
